import React from 'react';

import { Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { TreeContainer } from 'containers/Documents/components/FolderTreePicker/styles';

import { OrgUnit } from '../types';

import { PickerExpandIcon, PickerItemContent, PickerName, PickerRow } from './styles';

export interface UnitTreePickerProps {
  units: OrgUnit[];
  selectedId: string | null;
  expandedIds: Set<string>;
  onSelect: (unitId: string) => void;
  onToggleExpand: (unitId: string) => void;
  /** Units that cannot be chosen, with the reason shown on hover. */
  disabledReasons?: Map<string, string>;
}

const UnitTreePicker: React.FunctionComponent<UnitTreePickerProps> = ({
  units,
  selectedId,
  expandedIds,
  onSelect,
  onToggleExpand,
  disabledReasons,
}) => {
  const renderItem = (unit: OrgUnit, depth: number): React.ReactNode => {
    const hasChildren = !!unit.children?.length;
    const isExpanded = expandedIds.has(unit.uuid);
    const isSelected = selectedId === unit.uuid;
    const reason = disabledReasons?.get(unit.uuid);

    const row = (
      <PickerRow
        $depth={depth}
        $disabled={!!reason}
        role="button"
        tabIndex={0}
        aria-label={unit.name}
        aria-pressed={isSelected}
        aria-disabled={reason ? true : undefined}
        onClick={() => !reason && onSelect(unit.uuid)}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.target !== event.currentTarget) return;
          if ((event.key === 'Enter' || event.key === ' ') && !reason) {
            event.preventDefault();
            onSelect(unit.uuid);
          }
          if (event.key === 'ArrowRight' && hasChildren && !isExpanded) onToggleExpand(unit.uuid);
          if (event.key === 'ArrowLeft' && hasChildren && isExpanded) onToggleExpand(unit.uuid);
        }}
      >
        <PickerExpandIcon
          $visible={hasChildren}
          $expanded={isExpanded}
          role="button"
          aria-label={isExpanded ? `Collapse ${unit.name}` : `Expand ${unit.name}`}
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            onToggleExpand(unit.uuid);
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </PickerExpandIcon>
        <PickerItemContent $selected={isSelected}>
          <PickerName $selected={isSelected}>{unit.name}</PickerName>
        </PickerItemContent>
      </PickerRow>
    );

    return (
      <React.Fragment key={unit.uuid}>
        {reason ? (
          <Tooltip title={reason} placement="right">
            {row}
          </Tooltip>
        ) : (
          row
        )}
        {isExpanded && hasChildren ? unit.children?.map((child) => renderItem(child, depth + 1)) : null}
      </React.Fragment>
    );
  };

  return <TreeContainer data-testid="unit-tree-picker">{units.map((unit) => renderItem(unit, 0))}</TreeContainer>;
};

export default UnitTreePicker;
